import { useEffect, useState, useRef } from 'react';

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

// ============================================================
//     useWakeWord : Détection du mot de réveil
// Utilise la Web Speech API (SpeechRecognition) en continu
// Écoute "commencer la discussion / commande / lancer la discussion"
// Déclenche onWakeWord() → connecte Gemini Live API
// Active tant que isEnabled = true
// ============================================================
export function useWakeWord(onWakeWord: () => void, isEnabled: boolean) {
  const [isListening, setIsListening] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  
  // Prevent rapid restart loops
  const shouldRestartList = useRef(true);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setLocalError("non-supporté");
      return undefined;
    }

    if (!isEnabled) {
      shouldRestartList.current = false;
      setIsListening(false);
      console.log(`🔇 Écoute du mot de réveil désactivée (isEnabled=false)`);
      return undefined;
    }

    console.log(`🎤 Activation de l'écoute du mot de réveil (isEnabled=true)`);
    shouldRestartList.current = true;
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onstart = () => {
      console.log(`🎙️ Écoute du microphone démarrée`);
      setIsListening(true);
      setLocalError(null);
    };

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        if (event.results[i].isFinal) {
          const transcript = event.results[i][0].transcript.toLowerCase();
          console.log(`📝 Transcription détectée: "${transcript}"`);
          
          if (
            transcript.includes("commencer la discussion") ||
            transcript.includes("commencer la commande") ||
            transcript.includes("lancer la discussion")
          ) {
            console.log(`🎯 Mot de réveil DÉTECTÉ dans: "${transcript}"`);
            onWakeWord();
          }
        }
      }
    };

    recognition.onerror = (event: any) => {
      // Ignorer les erreurs bénignes pour éviter de spammer la console
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        console.warn("Speech error pour le déclencheur:", event.error);
      }
      
      // En cas de permission refusée ou erreur de capture bloquante, on arrête
      if (event.error === 'not-allowed') {
        setLocalError("permission refusée");
        shouldRestartList.current = false;
        setIsListening(false);
      } else if (event.error === 'audio-capture') {
        console.warn("Erreur 'audio-capture' temporaire, on va tenter de redémarrer...");
        // Ne pas mettre shouldRestartList à false pour permettre la relance dans onend
        setLocalError("erreur de microphone temporaire");
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      if (shouldRestartList.current) {
        // Un délai plus long (1 seconde) évite la boucle rapide CPU si le micro "s'emballe"
        setTimeout(() => {
          if (shouldRestartList.current) {
            try { 
              recognition.start(); 
            } catch(e) {
              // ignore error
            }
          }
        }, 1000);
      } else {
        setIsListening(false);
      }
    };

    try {
      recognition.start();
    } catch (e) {
      console.error(e);
    }

    return () => {
      shouldRestartList.current = false;
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [isEnabled, onWakeWord]);

  const stopListening = () => {
    shouldRestartList.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // ignore error
      }
    }
    setIsListening(false);
  };

  return { isListening, localError, stopListening };
}